import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  CaseMeta,
  CaseOutcome,
  CaseRevisionMeta,
  OutcomeResponse,
} from "../pages/cases/types";
import { useNavigate, useParams } from "react-router-dom";
import { useApi } from "./ApiContext";
import { useError } from "./ErrorContext";
import { useAppContext } from "./AppContext";
import {
  CaseMockResponse,
  OutcomeMockResponse,
  RevisionMockResponse,
} from "../onboarding/mock";
import OnboardingModal from "../components/misc/OnboardingModal";

interface CaseContextProps {
  caseMeta: CaseMeta | null;
  currentOutcome: CaseOutcome | null;
  revisionMeta: CaseRevisionMeta | null;
  /** True while case, revision, and outcome are being (re)fetched. */
  isCaseLoading: boolean;
}

const CaseContext = createContext<CaseContextProps | undefined>(undefined);

export const CaseProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [caseMeta, setCaseMeta] = useState<CaseMeta | null>(null);
  const [currentOutcome, setCurrentOutcome] = useState<CaseOutcome | null>(
    null
  );
  const [revisionMeta, setRevisionMeta] = useState<CaseRevisionMeta | null>(
    null
  );
  const [isCaseLoading, setIsCaseLoading] = useState(true);

  const { id: caseId, revisionId } = useParams();
  const { client } = useApi();
  const { showError } = useError();
  const { setIsLoading } = useAppContext();
  const navigate = useNavigate();

  useEffect(() => {
    if (!caseId) {
      setIsCaseLoading(false);
      return;
    }

    const fetchData = async () => {
      const isExample = caseId === CaseMockResponse.id;
      if (isExample) {
        setCaseMeta(CaseMockResponse);
        setRevisionMeta(RevisionMockResponse);
        setCurrentOutcome(OutcomeMockResponse);
        setIsCaseLoading(false);
        return;
      }

      setIsCaseLoading(true);
      try {
        const caseResponse = await client.GET("/v1/cases/{case_id}", {
          params: {
            path: {
              case_id: caseId,
            },
          },
        });

        if (caseResponse.error) {
          showError("Error fetching case data");
          navigate("/cases");
          return;
        }
        setCaseMeta(caseResponse.data as CaseMeta);

        const revisionResponse = await client.GET(
          "/v1/cases/{case_id}/revisions/{revision_id}",
          {
            params: {
              path: {
                case_id: caseId,
                revision_id: revisionId ? Number(revisionId) : "latest",
              },
            },
            // cacheable: true,
          }
        );
        if (revisionResponse.error) {
          setIsLoading(false);
          showError("Error fetching case data");
          navigate("/cases");
          return;
        }
        setRevisionMeta(revisionResponse.data as CaseRevisionMeta);

        const outcomeResposne = await client.GET(
          "/v1/cases/{case_id}/revisions/{revision_id}/outcome/",
          {
            params: {
              path: {
                case_id: caseId,
                revision_id: revisionId ? Number(revisionId) : "latest",
              },
            },
            // cacheable: true,
          }
        );
        const outcome = outcomeResposne.data as OutcomeResponse;
        setCurrentOutcome(outcome as CaseOutcome);
      } catch (error) {
        showError("Error fetching case data");
        return;
      } finally {
        setIsLoading(false);
        setIsCaseLoading(false);
      }
    };

    fetchData();
  }, [client, caseId, revisionId]);
  return (
    <CaseContext.Provider
      value={{
        caseMeta,
        currentOutcome,
        // setCaseMeta,
        // setCurrentOutcome,
        revisionMeta,
        isCaseLoading,
      }}
    >
      {children}
      <OnboardingModal />
    </CaseContext.Provider>
  );
};

export const useCase = (): CaseContextProps | undefined => {
  const context = useContext(CaseContext);
  if (!context) {
    return;
  }
  return context;
};
